import { useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@nextgen-web-framework/core';
import { Logger } from '@nextgen-web-framework/core';
import {
  AccountType,
  useSession,
  useBreakpoint,
  Breakpoints,
  useTranslation,
  useToast,
} from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  profileApi,
  updateProfileInformationData,
  updateAccountInformation,
  ProfileInformationType,
  AlternativeContactType,
  PhoneDetail,
  EmailDetail,
  PhoneCreate,
  EmailCreate,
  PhoneType,
  EmailType,
  SavePhoneDataType,
  RegFlowConfig,
  MinorAgeConfigType,
  Citizenship,
} from '@commerce-profile-webcomponents/sdk';
import {
  getCitizenshipByCountry,
  getAccountCitizenship,
  getAccountData,
  extractLifeCycleConfig,
} from './accountLifeCycleUtils';
import {
  getDateLocaleByCitizenship,
  parseString,
  parseNumber,
  extractPhoneNumber,
  parsePhoneNumber,
  parseArray,
  parseObject,
  defaultApiErrorHandler,
} from './helper';
import {
  useCountryMasterData,
  useMyAccountAddressConfig,
  useMyAccountProfileConfig,
  useRegistrationFlowConfig,
} from './masterdata';
import { useLocale, useGeneralConfig } from './customHooks';
import { useFeatureFlag } from './featureFlagHook';
import {
  getConfigByPK,
  MasterConfigNameConst,
  extractPersonalIds,
  getExt,
  ProfileSectionConst,
  generatePersonalInfoPayload,
  profileSavePhoneHandler,
  profileSaveEmailHandler,
} from './myAccountProfileUtils';
import { fetchAddressHandler, profileSaveAddressHandler } from './myAccountAddressUtils';
import { MyAccountExtEnum, MyAccountFieldName } from './types/enum';
import { MyAccountData, MyAccountInput, MyAccountOption } from './types/propTypes';
import { AccountLifeCycleConstants } from './Constants';

export const useMyAccount = () => {
  // Consolidate all requires data for common use
  const t = useTranslation();
  const { addToast } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const apiErrorHandler = defaultApiErrorHandler;
  const logger = Logger.getInstance();

  // Store current config data, use variable and state to keep the data
  let configList: MyAccountInput[] = [];
  let configExt: MyAccountOption[] = [];
  const [stateConfigList, setStateConfigList] = useState<MyAccountInput[]>([]);
  const [stateConfigExt, setStateConfigExt] = useState<MyAccountOption[]>([]);

  const { session, isMember, isABO } = useSession();
  const {
    id,
    aboNum: accountNum,
    uplineSponsorAccount: uplineSponsorAccountNum,
    partyId: partyIdValue,
    accountCountry,
    type,
  } = session as DynamicObjectInterface;

  const profileId = parseString(id);
  const aboNum = parseString(accountNum);
  const uplineSponsorAccount = parseString(uplineSponsorAccountNum);
  const partyId = parseNumber(partyIdValue);
  const accountCountryCode = parseString(accountCountry)?.toLowerCase();

  const profileConfig = useMyAccountProfileConfig();
  const addressConfig = useMyAccountAddressConfig();
  const { currentCountry } = useCountryMasterData();
  const defaultCountryDialCode = `(+${currentCountry?.phoneCode})`;
  const registrationFlowConfig = useRegistrationFlowConfig();

  const breakpoint = useBreakpoint();
  const isMobile = [Breakpoints.XS, Breakpoints.SM].includes(breakpoint as Breakpoints);
  const isTablet = [Breakpoints.MD].includes(breakpoint as Breakpoints);
  const isDesktop = ![Breakpoints.XS, Breakpoints.SM, Breakpoints.MD].includes(breakpoint as Breakpoints);

  const siteLocale = useLocale();
  const [shortLocaleCode] = siteLocale.split('-'); // make sure it's in short format

  // Config functions
  const getProfileConfig = () => getConfigByPK(profileConfig, MasterConfigNameConst.STEP_PROFILE_FORM);
  const getCoAppConfig = () => getConfigByPK(profileConfig, MasterConfigNameConst.STEP_CO_APP_FORM);
  const getMyBusinessConfig = () => getConfigByPK(profileConfig, MasterConfigNameConst.STEP_MY_BUSINESS_FORM);
  const getAccountLifeCycleConfig = () => getConfigByPK(profileConfig, MasterConfigNameConst.STEP_ACCOUNT_LIFE_CYCLE);

  const getGeneralConfig = useGeneralConfig;

  const featureFlags = useFeatureFlag();

  const extractAccountLifeCycleConfig = () => {
    const { inputs: accountLifeCycleConfig } = getAccountLifeCycleConfig();
    return extractLifeCycleConfig(accountLifeCycleConfig, type, featureFlags);
  };

  const getUpgradeMinorConfigurations = () => {
    const blankConfig: MinorAgeConfigType = { min: null, max: null };
    const accountLifeCycleConfig = getAccountLifeCycleConfig().inputs;
    const minorConfig = accountLifeCycleConfig.find(
      (input: MyAccountInput) => input.fieldName === AccountLifeCycleConstants.ALLOW_UPGRADE,
    ) as MyAccountInput;

    if (!minorConfig) {
      return { minorAge: null, inputs: [] };
    }

    const { minor, inputs: minorDocuments } = minorConfig;
    const upgradeConfig: MinorAgeConfigType = {
      ...blankConfig,
      ...(typeof minor === 'object' && minor !== null ? minor : {}),
    };

    // Get step name from regConfigFlow
    const upgradeRegConfig = (registrationFlowConfig?.[RegFlowConfig.UpgradeFlow] as { steps?: DynamicObjectInterface })
      ?.steps;
    const stepName = Object.keys(upgradeRegConfig || {})[0] || '';

    return { minorAge: upgradeConfig, inputs: minorDocuments || [], stepName };
  };

  const setConfig = (config: MyAccountData) => {
    // To set and initialize the config data
    const { inputs, ext } = config;

    setStateConfigList(inputs);
    setStateConfigExt(ext ?? []);
    configList = inputs;
    configExt = ext ?? [];
  };

  const getConfigList = () => (configList && configList.length > 0 ? configList : stateConfigList);
  const getConfigExt = () => (configExt && configExt.length > 0 ? configExt : stateConfigExt);

  const getConfigFromParam = (config?: MyAccountData) => {
    if (config) {
      const { inputs, ext } = config;
      return { inputs, ext };
    }
    // fallback to local var/state
    return { inputs: getConfigList(), ext: getConfigExt() };
  };

  const isEmailRequired = (config?: MyAccountData) => {
    const { inputs } = getConfigFromParam(config);
    return !!inputs
      .find((section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_CONTACT)
      ?.inputs?.find((input: MyAccountInput) => input.fieldName === MyAccountFieldName.EMAIL)?.validations?.required;
  };

  const isAddressEditable = (config?: MyAccountData) => {
    const { inputs } = getConfigFromParam(config);
    return !!inputs
      .find((section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_ADDRESS)
      ?.inputs?.some(
        (input: MyAccountInput) =>
          [MyAccountFieldName.MAILING_ADDRESS, MyAccountFieldName.BILLING_ADDRESS].includes(
            input.fieldName as MyAccountFieldName,
          ) && input.editable,
      );
  };

  const isAdditionalPhoneEnabled = (config?: MyAccountData) => {
    const { inputs } = getConfigFromParam(config);
    return (
      getExt(
        MyAccountExtEnum.ENABLE_ALTERNATIVE_NUMBER,
        inputs.find((section: MyAccountInput) => section.section === ProfileSectionConst.PROFILE_SECTION_CONTACT)?.ext,
      ) ?? false
    );
  };

  // Data fetching functions
  const fetchProfileData = async (
    _profileId: string,
    _aboNum: string,
    uplineSponsorAccount?: string,
    loadAddress = true,
    sliceUpdateHandler = updateProfileInformationData,
  ) => {
    // Get profile data

    const [profile, accountRes, contactData] = await Promise.all([
      // Profile data
      apiErrorHandler(() => profileApi.getProfilesById(_profileId)),
      // Get account data
      getAccountData(_aboNum, apiErrorHandler),
      // Get phone & email
      fetchAccountContactsData(_profileId, _aboNum),
    ]);

    const { govtRecordList = [] } = parseObject(profile);

    // Identify citizenship value
    const citizenshipValue = currentCountry
      ? getCitizenshipByCountry(currentCountry, profile?.citizenshipCountryCode, profile?.citizenship)
      : '';

    // Identify primary account citizenship
    const primaryCitizenship = await getAccountCitizenship(
      _aboNum,
      _profileId,
      accountRes?.accountType as AccountType,
      parseString(profile?.citizenship),
      apiErrorHandler,
    );

    // Extract taxId and idNumber from profile or account data
    const { taxId, idNumber, documentType } = extractPersonalIds(govtRecordList, accountRes);

    // Load sponsor data if account is provided
    const sponsorPromise = uplineSponsorAccount ? fetchUplineSponsorData(uplineSponsorAccount) : Promise.resolve(null);
    const sponsorData = await sponsorPromise;

    // Load addresses if required
    let mailingAddress: DynamicObjectInterface,
      billingAddress: DynamicObjectInterface,
      taxAddress: DynamicObjectInterface;
    if (loadAddress || citizenshipValue === Citizenship.Citizen) {
      const {
        mailingAddress: mailingAddr,
        billingAddress: billingAddr,
        taxAddress: taxAddr,
      } = await fetchAddressHandler(_aboNum, apiErrorHandler);
      mailingAddress = mailingAddr;
      billingAddress = billingAddr;
      taxAddress = taxAddr;
    }

    // Construct profile data for slice
    const profileInfoSlice: ProfileInformationType = {
      version: parseNumber(profile.version),
      profileData: {
        aboNumber: _aboNum,
        id: _profileId,

        // Personal data (Personal Information)
        firstName: parseString(profile?.displayName?.givenName),
        lastName: parseString(profile?.displayName?.familyName),
        titleSalutation: parseString(profile?.displayName?.prefix),
        birthDate: String(profile?.birthDate || ''),
        maritalStatus: parseString(profile?.maritalStatus),
        gender: parseString(profile?.gender),
        citizenship: parseString(citizenshipValue),
        citizenshipCountryCode: parseString(profile?.citizenshipCountryCode),
        // additional data not in form.
        characterType: profile.displayName?.characterType,
        preferredLanguageCd: profile?.preferredLanguageCd,
        idNumber,
        taxId,
        personalIdType: documentType,
        taxRegime: accountRes?.taxClass,

        // Account data (Account Renewal)
        aboName: accountRes?.name?.name,
        joinDate: accountRes?.entryDate,
        expiryDate: accountRes?.expirationDate,
        partyId: profile?.partyId,
        primaryCitizenship,
        ...accountRes,
        // Sponsor data
        ...sponsorData,

        // Contacts (Phone, Email)
        ...contactData,
      },
      mailingAddress,
      billingAddress,
      taxAddress,
    };

    dispatch(sliceUpdateHandler(profileInfoSlice));

    return profileInfoSlice;
  };

  const fetchProfileDataIfEmpty = async (
    _profileInformation: ProfileInformationType,
    _profileId: string,
    _aboNum: string,
    uplineSponsorAccount?: string,
    loadAddress = true,
  ) => {
    // Fetch profile data if empty, to ensure the data is available
    // use for upgrade/downgrade, renewal, etc.
    if (!_profileInformation?.profileData?.id) {
      return await fetchProfileData(_profileId, _aboNum, uplineSponsorAccount, loadAddress);
    }
    return _profileInformation;
  };

  const fetchCoAppData = async (_profileId: string, _aboNum: string) =>
    await fetchProfileData(_profileId, _aboNum, '', false, updateAccountInformation);

  const fetchAccountContactsData = async (_profileId: string, _aboNum: string) => {
    const contactData: DynamicObjectInterface = {
      phone: '',
      dialCode: defaultCountryDialCode,
      phoneId: '',
      phoneVersion: 1,
      fullPhoneNumber: '',
      email: '',
      emailId: '',
      emailVersion: 1,
      alternativePhone: [],
    };

    const parsePhone = (phone = '') =>
      extractPhoneNumber(parseString(phone)) || {
        dialCode: defaultCountryDialCode,
        number: '',
      };

    try {
      const [phoneRes, emailRes] = await Promise.all([
        apiErrorHandler(() => profileApi.getAccountPhones(_aboNum, _profileId, !isAdditionalPhoneEnabled())),
        apiErrorHandler(() => profileApi.getAccountEmails(_aboNum, _profileId)),
      ]);

      if (!phoneRes?.phoneList?.length) {
        logger.warn('Empty phone list for', _aboNum);
      }
      // Process Phone Data
      const defaultPhone = phoneRes?.phoneList?.find((phone: PhoneDetail) => phone?.isDefault);
      const additionalPhones = phoneRes?.phoneList?.filter((phone: PhoneDetail) => !phone?.isDefault);
      const { number, dialCode } = parsePhone(defaultPhone?.phone);

      Object.assign(contactData, {
        phone: number,
        dialCode,
        fullPhoneNumber: defaultPhone?.phone,
        phoneId: defaultPhone?.phoneId,
        phoneVersion: defaultPhone?.version ?? 1,
        alternativePhone:
          additionalPhones?.map((p: PhoneDetail, index) => {
            const { number, dialCode } = parsePhone(p.phone);
            return {
              id: index,
              phoneId: p.phoneId,
              type: p.phoneType,
              dialCode,
              number,
              phoneVersion: p.version,
              isUpdated: false,
            };
          }) || [],
      });

      // Process Email Data
      if (!emailRes?.emailList?.length) {
        logger.warn('Empty email list for', _aboNum);
      }
      const defaultEmail = emailRes?.emailList?.find((email: EmailDetail) => email?.isDefault);

      Object.assign(contactData, {
        email: defaultEmail?.email,
        emailId: defaultEmail?.emailId,
        emailVersion: defaultEmail?.version ?? 1,
      });
    } catch (error) {
      logger.error('No phone/email found for this account', error);
      // No action required for this case.
    }

    return contactData;
  };

  const fetchUplineSponsorData = async (_uplineSponsorAccount: string) => {
    // This is apply to main profile, not co-applicant
    let sponsorData: DynamicObjectInterface = {
      sponsorName: '',
      sponsorNumber: '',
      sponsorLocation: '',
    };

    try {
      const sponsorInfoRes = await apiErrorHandler(() => profileApi.getAccountInformation(_uplineSponsorAccount));
      sponsorData = {
        sponsorName: sponsorInfoRes?.name?.name,
        sponsorNumber: _uplineSponsorAccount,
        sponsorLocation: sponsorInfoRes?.countryCode,
      };
    } catch (_) {
      // no action required, show empty field
      logger.error("No data for sponsor's account");
    }

    return sponsorData;
  };

  // Data saving functions -------------------------------------------------------
  const saveProfileDataHandler = async (
    profile: ProfileInformationType,
    originalProfile: ProfileInformationType,
    profileId: string,
    accountNum: string,
    sliceUpdateHandler: Function,
  ) => {
    // handle empty dialCode
    if (!profile.profileData.dialCode) {
      profile.profileData.dialCode = defaultCountryDialCode;
    }

    // Call api for update, and after that dispatch the data
    // Update profile data
    const profileRes = await saveProfileDetailHandler(profile, originalProfile, profileId);
    // Update to state
    const updatedProfile: ProfileInformationType = {
      ...profile,
      version: profileRes?.version,
    };

    try {
      const contactRes = await saveAccountPhoneAndEmail(profileId, accountNum, profile, originalProfile);
      updatedProfile.profileData = {
        ...updatedProfile.profileData,
        ...contactRes,
      };
    } catch (error) {
      logger.error('Error saving phone and email to account api', error);
      throw error;
    }

    if (isAddressEditable()) {
      if (profile.mailingAddress && profile.mailingAddress.updated) {
        const mailingRes = await profileSaveAddressHandler(profile.mailingAddress, apiErrorHandler);
        updatedProfile.mailingAddress = {
          ...profile.mailingAddress,
          version: mailingRes?.version,
        };
      }

      if (profile.billingAddress && profile.billingAddress.updated) {
        const billingRes = await profileSaveAddressHandler(profile?.billingAddress, apiErrorHandler);
        updatedProfile.billingAddress = {
          ...profile.billingAddress,
          version: billingRes?.version,
        };
      }
    }

    // Update to state to avoid error on next save
    dispatch(sliceUpdateHandler(updatedProfile));

    return true;
  };

  const saveProfileDetailHandler = async (
    profile: ProfileInformationType,
    originalProfile: ProfileInformationType,
    profileId: string,
  ) => {
    const { profileData } = profile;

    const { profileData: originalProfileData } = originalProfile;

    logger.log('Save profile data - config', getConfigList(), getConfigExt());

    // Generate clean payload
    const payload = generatePersonalInfoPayload(profileData, originalProfileData, getConfigList());

    if (Object.keys(payload).length !== 0) {
      // skip if no data to update
      return await apiErrorHandler(() =>
        profileApi.patchProfilesById({
          payload: {
            profile: payload,
            version: profile.version,
          },
          profileId: profileId,
        }),
      );
    } else {
      return {
        version: profile.version,
      };
    }
  };

  const saveAccountPhoneAndEmail = async (
    profileId: string,
    accountNum: string,
    profile: ProfileInformationType,
    originalProfile: ProfileInformationType,
  ) => {
    // This function is use for saving phone and email to account api
    const { profileData } = profile;
    const {
      phoneId = '',
      phone = '',
      dialCode = '',
      phoneChallengeId = '',
      phoneVersion = 1,
      emailId = '',
      email = '',
      emailChallengeId = '',
      emailVersion = 1,
      alternativePhone,
    } = profileData;

    const { profileData: originalProfileData } = originalProfile;
    const { phone: originalPhone, dialCode: originalDialCode, email: originalEmail } = originalProfileData;

    const accountNumber = parseNumber(accountNum);

    let returnData: DynamicObjectInterface = {};
    // Phone -------------------------------------------------------------
    let phoneRes: DynamicObjectInterface = null;

    const isPhoneUpdated = (phone !== originalPhone || dialCode !== originalDialCode) && phoneChallengeId;
    // if phone is empty but having phoneId, then set as delete
    const isPhoneDeleted = phoneId && !phone;
    const phonePayload: PhoneCreate = {
      profileId: profileId,
      accountNumber: accountNumber,
      phoneId: phoneId,
      phone: parsePhoneNumber(dialCode, phone),
      challengeId: phoneChallengeId,
      version: phoneVersion,
      phoneType: PhoneType.Mobile,
      isDefault: true,
    };

    if (isPhoneUpdated) {
      phoneRes = await profileSavePhoneHandler(phonePayload, isPhoneDeleted, apiErrorHandler);
      returnData = {
        ...returnData,
        fullPhoneNumber: phonePayload.phone,
        phoneVersion: parseNumber(phoneRes?.version),
      };
    }

    // Email -------------------------------------------------------------
    let emailRes: DynamicObjectInterface = null;
    const isEmailUpdated = email !== originalEmail;
    // if email is empty but having emailId, then set as delete
    const isEmailDeleted = emailId && !email;
    const emailPayload: EmailCreate = {
      profileId: profileId,
      accountNumber: accountNumber,
      emailId: emailId,
      email: email,
      challengeId: emailChallengeId,
      version: emailVersion,
      emailType: EmailType.Personal,
      isDefault: true,
    };

    if (isEmailUpdated && (emailChallengeId || (!isEmailRequired() && isEmailDeleted))) {
      emailRes = await profileSaveEmailHandler(emailPayload, isEmailDeleted, apiErrorHandler);

      returnData = {
        ...returnData,
        email: emailPayload.email,
        emailVersion: parseNumber(emailRes?.version),
        emailId: emailRes?.emailId ?? null,
      };
    }

    // Alternative phone -------------------------------------------------
    if (isAdditionalPhoneEnabled()) {
      const altPhonePromises = alternativePhone
        ?.filter(({ isUpdated, isDeleted }: AlternativeContactType) => isUpdated || isDeleted)
        ?.map(async (altPhone: AlternativeContactType) => {
          const { phoneId, type, dialCode = '', number = '', phoneVersion, isDeleted } = altPhone;
          const phonePayload: PhoneCreate = {
            profileId: profileId,
            accountNumber: accountNumber,
            phoneId: phoneId,
            phone: parsePhoneNumber(dialCode, number),
            phoneType: type,
            isDefault: false,
            version: phoneVersion,
          };
          try {
            // Create/Update/Delete alternative phone
            const altPhoneRes = await profileSavePhoneHandler(phonePayload, isDeleted, apiErrorHandler);
            return {
              ...altPhone,
              phoneId: altPhoneRes?.phoneId,
              phoneVersion: altPhoneRes?.version,
            };
          } catch (error) {
            logger.error('Error saving alternative phone', error);
            throw error;
          }
        });

      await Promise.all(parseArray(altPhonePromises));

      returnData = {
        ...returnData,
        alternativePhone: altPhonePromises,
      };
    }
    return returnData;
  };

  // For external use
  const savePrimaryPhoneHandler = async (payload: SavePhoneDataType) => {
    const { profileId, accountNum, profileInformation, phoneNumber, challengeId } = payload;

    const { profileData } = profileInformation;

    const { phoneId, phoneVersion } = profileData;
    const accountNumber = parseNumber(accountNum);
    const phonePayload: PhoneCreate = {
      profileId: profileId,
      accountNumber: accountNumber,
      phoneId: phoneId,
      phone: phoneNumber,
      challengeId: challengeId,
      version: phoneVersion,
      phoneType: PhoneType.Mobile,
      isDefault: true,
    };

    const phoneRes = await profileSavePhoneHandler(phonePayload, undefined, apiErrorHandler);
    return {
      ...profileInformation,
      profileData: {
        ...profileData,
        fullPhoneNumber: phoneNumber,
        phoneVersion: phoneRes?.version ?? phoneVersion,
      },
    };
  };
  // Error handling --------------------------------------------------------------

  // Utility functions -----------------------------------------------------------
  const getLabel = (input: MyAccountInput, isAddress?: boolean) => {
    const { fieldName, ext } = input;
    const customLabel = getExt(MyAccountExtEnum.CUSTOM_LABEL_KEY, ext) as string;

    if (customLabel) {
      return t(customLabel);
    } else if (isAddress) {
      return t(`profile.AddressInput.${fieldName}`);
    } else {
      return t(`profile.ProfileInfo.${fieldName}`);
    }
  };

  const getDateLocale = (profileData: ProfileInformationType['profileData']) =>
    getDateLocaleByCitizenship(
      profileData?.citizenship,
      profileData?.citizenshipCountryCode,
      getGeneralConfig()?.defaultDateLocale,
    );

  return {
    // Profile data
    session,
    profileId,
    aboNum,
    partyId,
    uplineSponsorAccount,
    accountCountryCode,
    isMember,
    isABO,
    accountType: type,

    // Master data
    profileConfig,
    addressConfig,
    currentCountry,
    defaultCountryDialCode,

    // Breakpoint
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,

    // Locale
    locale: shortLocaleCode,

    dispatch,
    t,
    addToast,

    // Get masterdata config functions
    getProfileConfig,
    getCoAppConfig,
    getMyBusinessConfig,
    getAccountLifeCycleConfig,
    extractAccountLifeCycleConfig,
    getUpgradeMinorConfigurations,
    getGeneralConfig,

    setConfig,
    isEmailRequired,
    isAdditionalPhoneEnabled,
    isAddressEditable,
    usingAccountApiForContacts: () => true, // always use account api for contacts. Remove in next release

    // Profile data functions
    fetchProfileData,
    fetchProfileDataIfEmpty,
    fetchCoAppData,
    fetchUplineSponsorData,
    saveProfileDataHandler,

    savePrimaryPhoneHandler,

    // utility functions
    getLabel,
    getDateLocale,
  };
};
